import { PdfViewerLayoutType } from './../../+types/pdf-viewer-layout.type';
import { PdfViewerToggleThumbnails, PdfViewerZoomOutEvent, PdfViewerZoomInEvent, PdfViewerSwitchLayoutEvent } from './../../+types/pdf-viewer-event.type';
import { PDF_VIEWER_SVG_ICONS, PdfViewerIconType } from './../../+types/pdf-viewer-icon.type';
import { PdfViewerToolbarAction } from './../../+types/pdf-viewer-toolbar-config.type';
import { PdfViewerTranslationKey } from './../../+types/pdf-viewer-translations.type';
import PdfViewerToolbarButtonComponent from '../pdf-viewer-toolbar-button/pdf-viewer-toolbar-button.component';
import PdfViewerToolbarButtonGroupComponent from '../pdf-viewer-toolbar-button-group/pdf-viewer-toolbar-button-group.component';
const TOOLBAR_INITIAL_VISIBILITY_DURATION = 5000;
const TOOLBAR_VISIBILITY_DURATION = 2000;
export class PdfViewerToolbarComponent {
    constructor(hostElement, eventBus, config, translations) {
        this.hostElement = hostElement;
        this.eventBus = eventBus;
        this.config = config;
        this.translations = translations;
        this.prefix = 'sp-pdf-viewer';
        this.keepVisible = false;
        this.hideToolbarAfterTimeout = (timeoutInMilliseconds) => {
            clearTimeout(this.timeoutToHideToolbar);
            this.show();
            this.timeoutToHideToolbar = setTimeout(() => {
                if (!this.keepVisible) {
                    this.hide();
                }
            }, timeoutInMilliseconds);
        };
        this.hide = () => {
            var _a;
            (_a = this.element) === null || _a === void 0 ? void 0 : _a.classList.add('hidden');
        };
        this.show = () => {
            var _a;
            (_a = this.element) === null || _a === void 0 ? void 0 : _a.classList.remove('hidden');
        };
        this.onMouseOver = () => {
            this.keepVisible = true;
            clearTimeout(this.timeoutToHideToolbar);
            this.show();
        };
        this.onMouseOut = () => {
            this.keepVisible = false;
            this.hideToolbarAfterTimeout(TOOLBAR_VISIBILITY_DURATION);
        };
        this.onButtonFocus = () => {
            this.keepVisible = true;
            clearTimeout(this.timeoutToHideToolbar);
            this.show();
        };
        this.onButtonBlur = () => {
            this.keepVisible = false;
            this.hideToolbarAfterTimeout(TOOLBAR_VISIBILITY_DURATION);
        };
        this.onDocumentMouseEvent = (event) => {
            this.hideToolbarAfterTimeout(TOOLBAR_VISIBILITY_DURATION);
        };
        this.render = () => {
            if (!this.config) {
                return;
            }
            if (this.element) {
                this.destroy();
            }
            this.element = document.createElement('div');
            this.element.classList.add(`${this.prefix}-toolbar`);
            this.element.setAttribute('data-test-label', 'floatingToolbar');
            this.element.addEventListener('mouseover', this.onMouseOver);
            this.element.addEventListener('mouseout', this.onMouseOut);
            this.hostElement.addEventListener('mousemove', this.onDocumentMouseEvent);
            this.hostElement.addEventListener('mousedown', this.onDocumentMouseEvent);
            this.hideToolbarAfterTimeout(TOOLBAR_INITIAL_VISIBILITY_DURATION);
            if (this.config.actions.includes(PdfViewerToolbarAction.ToggleThumbnails)) {
                this.toggleThumbnailsGroup = new PdfViewerToolbarButtonGroupComponent(this.element);
                this.toggleThumbnailsGroup.render();
                const toggleThumbnailsButtonConfig = {
                    hostElement: this.toggleThumbnailsGroup.element,
                    icon: PDF_VIEWER_SVG_ICONS[PdfViewerIconType.ThumbnailsOutlined],
                    action: () => {
                        this.eventBus.dispatch(new PdfViewerToggleThumbnails());
                    },
                    label: this.translations[PdfViewerTranslationKey.ToggleThumbnails],
                    testLabel: 'floatingToolbarButtonTogglePages',
                    onFocus: this.onButtonFocus,
                    onBlur: this.onButtonBlur
                };
                this.toggleThumbnailsButton = new PdfViewerToolbarButtonComponent(toggleThumbnailsButtonConfig);
                this.toggleThumbnailsButton.render();
            }
            if (this.config.actions.includes(PdfViewerToolbarAction.ZoomIn) && this.config.actions.includes(PdfViewerToolbarAction.ZoomOut)) {
                this.zoomGroup = new PdfViewerToolbarButtonGroupComponent(this.element);
                this.zoomGroup.render();
                const zoomOutButtonConfig = {
                    hostElement: this.zoomGroup.element,
                    icon: PDF_VIEWER_SVG_ICONS[PdfViewerIconType.ZoomOutOutlined],
                    action: () => {
                        this.eventBus.dispatch(new PdfViewerZoomOutEvent());
                    },
                    label: this.translations[PdfViewerTranslationKey.ZoomOut],
                    testLabel: 'floatingToolbarButtonZoomOut',
                    onFocus: this.onButtonFocus,
                    onBlur: this.onButtonBlur
                };
                this.zoomOutButton = new PdfViewerToolbarButtonComponent(zoomOutButtonConfig);
                this.zoomOutButton.render();
                const zoomInButtonConfig = {
                    hostElement: this.zoomGroup.element,
                    icon: PDF_VIEWER_SVG_ICONS[PdfViewerIconType.ZoomInOutlined],
                    action: () => {
                        this.eventBus.dispatch(new PdfViewerZoomInEvent());
                    },
                    label: this.translations[PdfViewerTranslationKey.ZoomIn],
                    testLabel: 'floatingToolbarButtonZoomIn',
                    onFocus: this.onButtonFocus,
                    onBlur: this.onButtonBlur
                };
                this.zoomInButton = new PdfViewerToolbarButtonComponent(zoomInButtonConfig);
                this.zoomInButton.render();
            }
            if (this.config.actions.includes(PdfViewerToolbarAction.SwitchLayout)) {
                this.switchLayoutGroup = new PdfViewerToolbarButtonGroupComponent(this.element);
                this.switchLayoutGroup.render();
                const singlePageButtonConfig = {
                    hostElement: this.switchLayoutGroup.element,
                    icon: PDF_VIEWER_SVG_ICONS[PdfViewerIconType.OnePageViewOutlined],
                    action: () => {
                        this.eventBus.dispatch(new PdfViewerSwitchLayoutEvent({ layout: PdfViewerLayoutType.SinglePage }));
                    },
                    label: this.translations[PdfViewerTranslationKey.SwitchToSinglePageView],
                    testLabel: 'floatingToolbarButtonSwitchToSinglePageLayout',
                    onFocus: this.onButtonFocus,
                    onBlur: this.onButtonBlur
                };
                this.singlePageButton = new PdfViewerToolbarButtonComponent(singlePageButtonConfig);
                this.singlePageButton.render();
                const continuousScrollButtonConfig = {
                    hostElement: this.switchLayoutGroup.element,
                    icon: PDF_VIEWER_SVG_ICONS[PdfViewerIconType.OnePageScrollingOutlined],
                    action: () => {
                        this.eventBus.dispatch(new PdfViewerSwitchLayoutEvent({ layout: PdfViewerLayoutType.OneColumn }));
                    },
                    label: this.translations[PdfViewerTranslationKey.SwitchToContinuousScrollView],
                    testLabel: 'floatingToolbarButtonSwitchToContinuousScrollMode',
                    onFocus: this.onButtonFocus,
                    onBlur: this.onButtonBlur
                };
                this.continuousScrollButton = new PdfViewerToolbarButtonComponent(continuousScrollButtonConfig);
                this.continuousScrollButton.render();
            }
            this.hostElement.appendChild(this.element);
        };
        this.destroy = () => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            this.element.removeEventListener('mouseover', this.onMouseOver);
            this.element.removeEventListener('mouseout', this.onMouseOut);
            this.hostElement.removeEventListener('mousemove', this.onDocumentMouseEvent);
            this.hostElement.removeEventListener('mousedown', this.onDocumentMouseEvent);
            (_a = this.toggleThumbnailsButton) === null || _a === void 0 ? void 0 : _a.destroy();
            (_b = this.toggleThumbnailsGroup) === null || _b === void 0 ? void 0 : _b.destroy();
            (_c = this.zoomOutButton) === null || _c === void 0 ? void 0 : _c.destroy();
            (_d = this.zoomInButton) === null || _d === void 0 ? void 0 : _d.destroy();
            (_e = this.zoomGroup) === null || _e === void 0 ? void 0 : _e.destroy();
            (_f = this.singlePageButton) === null || _f === void 0 ? void 0 : _f.destroy();
            (_g = this.continuousScrollButton) === null || _g === void 0 ? void 0 : _g.destroy();
            (_h = this.switchLayoutGroup) === null || _h === void 0 ? void 0 : _h.destroy();
            clearTimeout(this.timeoutToHideToolbar);
            this.hostElement.removeChild(this.element);
            this.element = null;
        };
    }
    disableToggleThumbnails() {
        this.toggleThumbnailsButton.isDisabled = true;
    }
    enableToggleThumbnails() {
        this.toggleThumbnailsButton.isDisabled = false;
    }
}
//# sourceMappingURL=pdf-viewer-toolbar.component.js.map